import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import {
  ColumnsMenu,
  PageHeader,
  PersonAvatar,
  SkillChips,
  useStoredListView,
  ViewSwitch,
} from '@/components/core';
import { PersonCard, PersonCardGrid } from '@/components/core/person-card-grid/person-card-grid';
import { PRESENCE_TEXT_TONE } from '@/components/core/presence-avatar/presence-avatar';
import { cn } from '@/lib/utils';
import { presenceText } from '../../utils/employee-presence';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui-kit/tabs';
import { useUrlTab } from '@/hooks/use-linked-record';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { useServerTable } from '@/hooks/use-server-table';
import { useToast } from '@/hooks/use-toast';
import { copyTextToClipboard } from '@/lib/clipboard';
import { mergeWhere } from '@/lib/table-query';
import { useAuthStore } from '@/state/store/auth';
import { useDepartmentScope } from '@/modules/shared/hr-lookups';
import {
  createEmployeeTableColumns,
  EMPLOYEE_HIDDEN_COLUMNS,
  EmployeeActions,
  employeeOptionalColumns,
} from '../../components/employee-table-columns/employee-table-columns';
import { useEmployeeColumnWhere } from '../../hooks/use-employee-column-where';
import { employeeName } from '../../schemas/display-name';
import { useEmployeeListLookups } from '../../hooks/use-employee-list-lookups';
import { FineDialog } from '../../components/fine-dialog/fine-dialog';
import { RoleAssignDialog } from '../../components/role-assign-dialog/role-assign-dialog';
import { WfoDaysDialog } from '../../components/work-status/wfo-days-dialog';
import {
  useDeactivatedUserIds,
  useEmployeeCurrentWorkDays,
  useEmployeesLoginActive,
  useGetEmployees,
  useSetEmployeeActive,
  useUpdateEmployeeWorkStatus,
} from '../../hooks/use-employees';
import { useEmployeesPresence } from '../../hooks/use-employee-presence';
import { DEFAULT_EMPLOYEES_ORDER } from '../../graphql/queries';
import { workFromHomeOff, workFromHomeOn, WorkStatusChanges } from '../../schemas/work-status';
import { Employee, EmployeesPagingResult } from '../../types/employee.types';
import { useReportUnreportedError } from '@/state/query-client/hooks';
import { APP_PATHS, appPath, PEOPLE_TABS, PeopleTab } from '@/constant/app-paths';

export const EmployeeListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const canCreate = useHasPermission(HRM_PERMISSIONS.PEOPLE.CREATE);
  const canUpdate = useHasPermission(HRM_PERMISSIONS.PEOPLE.UPDATE);
  const currentUserId = useAuthStore((state) => state.user?.itemId);
  const canChangeWfh = useHasPermission(HRM_PERMISSIONS.PEOPLE.CHANGE_WFH);
  const canCreateFine = useHasPermission(HRM_PERMISSIONS.PEOPLE.CREATE_FINE);
  const canAssignRole = useHasPermission(HRM_PERMISSIONS.PEOPLE.ASSIGN_ROLE);
  // Without DEPARTMENT.VIEW_ALL_DEPARTMENTS, the directory is limited to the departments whose Head
  // of Department role the person holds (legacy "HOB" behavior: a department head only ever saw their
  // own department's employees).
  const { departments, getDepartmentIdCondition } = useDepartmentScope();
  const [statusTarget, setStatusTarget] = useState<Employee | null>(null);
  // The header spot the cards' filter button is rendered into on a phone.
  const [filterSlot, setFilterSlot] = useState<HTMLSpanElement | null>(null);
  const [officeDaysTarget, setOfficeDaysTarget] = useState<Employee | null>(null);
  const { data: officeTargetWorkDays, isLoading: isLoadingOfficeTargetWorkDays } =
    useEmployeeCurrentWorkDays(officeDaysTarget?.UserId);
  const [fineTarget, setFineTarget] = useState<Employee | null>(null);
  const [roleTarget, setRoleTarget] = useState<Employee | null>(null);
  const {
    mutateAsync: updateWorkStatus,
    isPending: isSavingWorkStatus,
    variables: workStatusVariables,
  } = useUpdateEmployeeWorkStatus();
  const { mutateAsync: setEmployeeActive, isPending: isChangingStatus } = useSetEmployeeActive();
  const reportError = useReportUnreportedError();

  const { pagination, resetPage, order, columnWhere, tableProps } = useServerTable({
    pageSize: 25,
    defaultOrder: DEFAULT_EMPLOYEES_ORDER,
  });

  // Employees / Alumni (agreed 2 Nov 2026): alumni are the people whose login is deactivated in IAM.
  const [tab, setTab] = useUrlTab<PeopleTab>(
    [PEOPLE_TABS.EMPLOYEES, PEOPLE_TABS.ALUMNI],
    PEOPLE_TABS.EMPLOYEES
  );
  const isAlumni = tab === PEOPLE_TABS.ALUMNI;
  const { data: deactivatedIds, isLoading: isLoadingDeactivated } = useDeactivatedUserIds();
  useEffect(() => {
    resetPage();
  }, [tab, resetPage]);

  // The name, ID and email are filtered from their columns; this keeps the department scope.
  const departmentCondition = getDepartmentIdCondition('');
  // The Data Gateway has no `nin`: Employees leaves out each deactivated login with its own `neq`
  // (there are few of them).
  const filter = {
    ...(departmentCondition ? { DepartmentId: departmentCondition } : {}),
    ...(isAlumni
      ? { UserId: { $in: deactivatedIds ?? [] } }
      : deactivatedIds?.length
        ? { $and: deactivatedIds.map((userId) => ({ UserId: { $ne: userId } })) }
        : {}),
  };

  const profileWhere = useEmployeeColumnWhere(columnWhere);
  const { data, isLoading, error } = useGetEmployees({
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filter: mergeWhere(filter, profileWhere),
    order,
  });
  const result = (data as { Employees?: EmployeesPagingResult })?.Employees;
  // Nothing until the deactivated logins are known: the split would be wrong without them.
  const items = useEmployeesLoginActive(isLoadingDeactivated ? undefined : result?.items);
  const totalCount = isLoadingDeactivated ? 0 : (result?.totalCount ?? 0);

  // Today's presence (prototype People › Status, agreed 3 Nov 2026): only on Employees, and only for
  // viewers who may read everyone's attendance; nobody else runs these queries.
  const canSeePresence =
    useHasPermission(HRM_PERMISSIONS.TIME_ATTENDANCE.ATTENDANCE_VIEW_ALL) && !isAlumni;
  const pageUserIds = useMemo(
    () => items.map((employee) => employee.UserId ?? '').filter(Boolean),
    [items]
  );
  const presence = useEmployeesPresence(pageUserIds, canSeePresence);

  /** Saves the change; a failure is reported and keeps the dialog open. */
  const saveWorkStatus = async (employee: Employee, changes: WorkStatusChanges) => {
    try {
      await updateWorkStatus({ employee, changes });
      return true;
    } catch (error) {
      reportError(error);
      return false;
    }
  };

  const handleWfhChange = (employee: Employee, next: boolean) => {
    if (next) {
      void saveWorkStatus(employee, workFromHomeOn(employee));
      return;
    }
    // Everyone picks their office days before work from home turns off.
    setOfficeDaysTarget(employee);
  };

  const handleOfficeDaysConfirm = async (days: string[]) => {
    if (!officeDaysTarget) return;
    const changes = workFromHomeOff(days);
    if (changes && (await saveWorkStatus(officeDaysTarget, changes))) setOfficeDaysTarget(null);
  };

  const handleCopyEmail = async (employee: Employee) => {
    const copied = employee.Email ? await copyTextToClipboard(employee.Email) : false;
    toast(
      copied ? { title: t('EMAIL_COPIED') } : { variant: 'destructive', title: t('COPY_FAILED') }
    );
  };

  const handleCopyEmployeeId = async (employee: Employee) => {
    const copied = employee.EmployeeID ? await copyTextToClipboard(employee.EmployeeID) : false;
    toast(
      copied
        ? { title: t('EMPLOYEE_ID_COPIED') }
        : { variant: 'destructive', title: t('COPY_FAILED') }
    );
  };

  const handleRowClick = (employee: Employee) => navigate(appPath.person(employee.ItemId));
  const lookups = useEmployeeListLookups(items);
  // List or Cards (prototype viewSeg), remembered per tab; phones always show their own cards.
  const [employeesView, setEmployeesView] = useStoredListView('people-employees');
  const [alumniView, setAlumniView] = useStoredListView('people-alumni');
  const view = isAlumni ? alumniView : employeesView;
  const setView = isAlumni ? setAlumniView : setEmployeesView;

  const workStatusProps = canChangeWfh
    ? {
        savingEmployeeId: isSavingWorkStatus ? workStatusVariables?.employee.ItemId : undefined,
        onWfhChange: handleWfhChange,
        onOfficeDaysEdit: setOfficeDaysTarget,
      }
    : undefined;

  const columns = createEmployeeTableColumns({
    t,
    lookups,
    onView: handleRowClick,
    onEdit: canUpdate ? (employee) => navigate(appPath.personEdit(employee.ItemId)) : undefined,
    onToggleActive: canUpdate ? setStatusTarget : undefined,
    currentUserId,
    onCopyEmail: handleCopyEmail,
    onCopyEmployeeId: handleCopyEmployeeId,
    onFine: canCreateFine ? setFineTarget : undefined,
    onAssignRole: canAssignRole ? setRoleTarget : undefined,
    departments,
    presence: canSeePresence ? presence : undefined,
    workStatus: workStatusProps,
  });

  const isReactivating = statusTarget?.IsActive === false;
  const handleStatusConfirm = async () => {
    if (!statusTarget) return;
    try {
      await setEmployeeActive({ employee: statusTarget, isActive: isReactivating });
      setStatusTarget(null);
    } catch (error) {
      // The dialog stays open so it can be retried.
      reportError(error);
    }
  };

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader
        // On a phone the cards' filter button sits here, left of Add Employee.
        beforeActions={<span ref={setFilterSlot} className="contents md:hidden" />}
        title={t('PEOPLE')}
        actions={
          canCreate
            ? [
                {
                  label: t('ADD_EMPLOYEE'),
                  icon: <Plus className="size-5" />,
                  onClick: () => navigate(APP_PATHS.PEOPLE_NEW),
                },
              ]
            : []
        }
      />

      <Tabs value={tab} onValueChange={(value) => setTab(value as PeopleTab)}>
        <TabsList>
          <TabsTrigger value={PEOPLE_TABS.EMPLOYEES}>{t('PEOPLE_TAB_EMPLOYEES')}</TabsTrigger>
          <TabsTrigger value={PEOPLE_TABS.ALUMNI}>{t('PEOPLE_TAB_ALUMNI')}</TabsTrigger>
        </TabsList>
      </Tabs>

      <AdvanceDataTable
        // Each tab keeps its own columns choice, so the table starts afresh when the tab changes.
        key={tab}
        {...tableProps}
        data={items}
        columns={columns}
        onRowClick={handleRowClick}
        isLoading={isLoading || isLoadingDeactivated}
        error={error instanceof Error ? error : null}
        pagination={{
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          totalCount,
        }}
        isExpandRowContent={false}
        defaultColumnVisibility={EMPLOYEE_HIDDEN_COLUMNS}
        columnVisibilityKey={isAlumni ? 'people-alumni' : 'people-employees'}
        columnsToolbar={(table) => (
          <div className="flex items-center justify-end gap-2 max-md:hidden">
            {view === 'list' && (
              <ColumnsMenu
                table={table}
                columns={employeeOptionalColumns(t, canSeePresence)}
                defaults={EMPLOYEE_HIDDEN_COLUMNS}
              />
            )}
            <ViewSwitch value={view} onChange={setView} />
          </div>
        )}
        renderCards={
          view === 'cards'
            ? (rows) => (
                <PersonCardGrid>
                  {rows.map(({ original: employee }) => {
                    const name = employeeName(employee);
                    const status = presence.byUserId[employee.UserId ?? ''];
                    return (
                      <PersonCard
                        key={employee.ItemId}
                        label={name}
                        onOpen={() => handleRowClick(employee)}
                        name={name}
                        role={employee.Designation}
                        meta={[employee.DepartmentName, employee.EmployeeID]
                          .filter(Boolean)
                          .join(' · ')}
                        chips={
                          lookups.skillsOf(employee).length > 0 ? (
                            <SkillChips skills={lookups.skillsOf(employee)} />
                          ) : undefined
                        }
                        avatar={
                          <PersonAvatar
                            name={name}
                            size={40}
                            presence={canSeePresence && status ? status.presence : undefined}
                            presenceLabel={canSeePresence && status ? ` · ` : undefined}
                          />
                        }
                        status={
                          canSeePresence && status ? (
                            <span
                              className={cn('font-medium', PRESENCE_TEXT_TONE[status.presence])}
                            >
                              {presenceText(t, status.label)}
                            </span>
                          ) : undefined
                        }
                        menu={
                          <EmployeeActions
                            employee={employee}
                            t={t}
                            currentUserId={currentUserId}
                            onView={handleRowClick}
                            onEdit={
                              canUpdate
                                ? (target) => navigate(appPath.personEdit(target.ItemId))
                                : undefined
                            }
                            onAssignRole={canAssignRole ? setRoleTarget : undefined}
                            onFine={canCreateFine ? setFineTarget : undefined}
                            onToggleActive={canUpdate ? setStatusTarget : undefined}
                            workStatus={workStatusProps}
                          />
                        }
                      />
                    );
                  })}
                </PersonCardGrid>
              )
            : undefined
        }
        mobileView="cards"
        mobileFilterSlot={filterSlot}
      />

      <WfoDaysDialog
        open={officeDaysTarget !== null}
        onOpenChange={(open) => !open && setOfficeDaysTarget(null)}
        initialDays={officeDaysTarget?.WFODays}
        workDays={officeTargetWorkDays}
        isLoadingWorkDays={isLoadingOfficeTargetWorkDays}
        isSaving={isSavingWorkStatus}
        onConfirm={handleOfficeDaysConfirm}
      />

      <FineDialog
        open={fineTarget !== null}
        onOpenChange={(open) => !open && setFineTarget(null)}
        employee={fineTarget}
      />

      <RoleAssignDialog
        open={roleTarget !== null}
        onOpenChange={(open) => !open && setRoleTarget(null)}
        employee={roleTarget}
      />

      <ConfirmationModal
        open={statusTarget !== null}
        onOpenChange={(open) => !open && setStatusTarget(null)}
        title={isReactivating ? t('REACTIVATE_USER') : t('DEACTIVATE_USER')}
        description={
          isReactivating
            ? t('ARE_YOU_SURE_TO_REACTIVATE_USER')
            : t('ARE_YOU_SURE_TO_DEACTIVATE_USER')
        }
        confirmText={isReactivating ? t('REACTIVATE') : t('DEACTIVATE')}
        preventAutoClose={isChangingStatus}
        onConfirm={handleStatusConfirm}
      />
    </div>
  );
};
